import React, { useState, useEffect, useCallback, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";

//BootstrapTable
import BootstrapTable from "react-bootstrap-table-next";

//paginator
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
} from "react-bootstrap-table2-paginator";

//DatePicker
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

//untils
import { LoaderText } from "@utils";

//Actions
import { actReportUsers, actDeactivateUserAccount } from "@actions";

//DataEmty
import { DataEmty } from "@components/common";

//Icon
import { Iconsax } from "@components-common";

//components
import { TableHeaderUsers, StatusBadge, DeactivateButton, DeactivateModal, FilterContainerUsers } from "./";

//styled-components
import {
    SearchContainerStyles,
    TableContainer,
    ContentTableWrapper,
    SearchContainer,
    SearchFieldArea,
    SearchInner,
    SearchField,
    SearchFieldInner,
    SearchIconContainer,
    SearchInputContainer,
    FakePlaceholder,
    ClearButton,
    DatePickerWrapper,
    ClearFiltersButton,
    FilterSpacer,
    PaginationWrapper,
    StatusCellWrapper,
    ActionsCellWrapper
} from './TableUsers.styled';
import {
    DatePickerButton,
    DatePickerText
} from '../styled/DatePicker.styled';

//partents
import { SortCaret } from "@ui-partents/Table";

// Import the pagination configuration
import { paginationOptions } from "@utils";

// Map API response to expected format
const mapApiResponseToUsers = (accounts) => {
    if (!accounts || !Array.isArray(accounts)) {
        return [];
    }

    return accounts.map((account, index) => {
        // API returns "First Name", "Last Name", "Email", "Joined Date", "Status", "Ward"
        const firstName = account["First Name"] || "";
        const lastName = account["Last Name"] || "";
        const email = account["Email"] || "";
        const joinedDate = account["Joined Date"] || "";
        const status = account["Status"] || "";
        const ward = account["Ward"] || "";
        
        // Format date if exists
        let formattedDate = "";
        if (joinedDate) {
            try {
                const date = new Date(joinedDate);
                formattedDate = date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            } catch (e) {
                formattedDate = "";
            }
        }
        
        return {
            user_id: index + 1, // Generate user_id from index
            first_name: firstName,
            last_name: lastName,
            email: email,
            ward: ward, // Map Ward from API
            join_date: formattedDate,
            join_date_original: joinedDate, // Keep original date for filtering
            status: status || "" // Map status from API (can be "inactive" lowercase)
        };
    });
};

function TableUsers(props) {
    const dispatch = useDispatch();
    
    // Get data from Redux store
    const reportUsersState = useSelector(state => state.reportUsers || {});
    const { data: reportUsersData, isLoading: isLoadingUsers, error: reportUsersError } = reportUsersState;
    
    const deactivateUserAccountState = useSelector(state => state.deactivateUserAccount || {});
    const { isLoading: isLoadingDeactivate, data: deactivateData, error: deactivateError } = deactivateUserAccountState;
    
    //state
    const [usersDataState, setUsersDataState] = useState([]);
    const [rowChecked, setRowChecked] = useState([]);
    const [modal, setModal] = useState({
        deactivate: false
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedUsersForBulk, setSelectedUsersForBulk] = useState([]);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    
    // Track deactivation operations
    const [deactivationInProgress, setDeactivationInProgress] = useState(false);
    const [deactivationType, setDeactivationType] = useState(null); // 'single' or 'bulk'
    const [pendingUserIds, setPendingUserIds] = useState([]);

    // useCallback for fetchDataUsers
    const fetchDataUsers = useCallback(() => {
        dispatch(actReportUsers());
    }, [dispatch]);

    // useCallback for toggleModal
    const toggleModal = useCallback((type) => {
        setModal(prevState => {
            const newState = { ...prevState, [type]: !prevState[type] };
            // Reset selections when closing modal
            if (prevState[type]) {
                setSelectedUser(null);
                setSelectedUsersForBulk([]);
            }
            return newState;
        });
    }, []);
    
    // Log error if any
    useEffect(() => {
        if (reportUsersError) {
            console.error('TableUsers - Error fetching users:', reportUsersError);
        }
    }, [reportUsersError]);

    // Handle deactivation success
    useEffect(() => {
        if (deactivateData && !isLoadingDeactivate && deactivationInProgress) {
            if (deactivationType === 'bulk' && pendingUserIds.length > 0) {
                // Bulk deactivation - update all pending users
                setUsersDataState(prevState => 
                    prevState.map(user => 
                        pendingUserIds.includes(user.user_id)
                            ? { ...user, status: "Deactivated" }
                            : user
                    )
                );
                setPendingUserIds([]);
                setDeactivationInProgress(false);
                setDeactivationType(null);
                toggleModal('deactivate');
                setRowChecked([]);
                setSelectedUser(null);
                fetchDataUsers();
            } else if (deactivationType === 'single' && selectedUser) {
                // Single user deactivation completed
                setUsersDataState(prevState => 
                    prevState.map(u => 
                        u.user_id === selectedUser.user_id 
                            ? { ...u, status: "Deactivated" }
                            : u
                    )
                );
                setDeactivationInProgress(false);
                setDeactivationType(null);
                toggleModal('deactivate');
                setRowChecked([]);
                setSelectedUser(null);
                fetchDataUsers();
            }
        }
    }, [deactivateData, isLoadingDeactivate, deactivationInProgress, deactivationType, pendingUserIds, selectedUser, fetchDataUsers, toggleModal]);

    // Handle deactivation error
    useEffect(() => {
        if (deactivateError && !isLoadingDeactivate && deactivationInProgress) {
            console.error('TableUsers - Error deactivating user:', deactivateError);
            setDeactivationInProgress(false);
            setDeactivationType(null);
            setPendingUserIds([]);
            // Optionally show error toast here
        }
    }, [deactivateError, isLoadingDeactivate, deactivationInProgress]);

    // Update usersDataState when Redux data changes
    useEffect(() => {
        if (reportUsersData && reportUsersData.accounts && Array.isArray(reportUsersData.accounts)) {
            const mappedUsers = mapApiResponseToUsers(reportUsersData.accounts);
            setUsersDataState(mappedUsers);
        } else if (!isLoadingUsers) {
            // Only reset if not loading (to avoid clearing during initial load)
            setUsersDataState([]);
        }
    }, [reportUsersData, isLoadingUsers]);

    //load Users
    useEffect(() => {
        fetchDataUsers();
    }, [fetchDataUsers]);

    // Effect to handle focus/blur on search input
    useEffect(() => {
        const handleFocus = () => setIsSearchFocused(true);
        const handleBlur = () => setIsSearchFocused(false);
        
        const searchInput = document.querySelector('.search-container .search_field input');
        if (searchInput) {
            searchInput.addEventListener('focus', handleFocus);
            searchInput.addEventListener('blur', handleBlur);
            
            return () => {
                searchInput.removeEventListener('focus', handleFocus);
                searchInput.removeEventListener('blur', handleBlur);
            };
        }
    }, [usersDataState]); // Re-run when data changes to re-attach listeners

    // Filter users by date range
    const filteredUsersData = React.useMemo(() => {
        if (!startDate || !endDate) {
            return usersDataState;
        }
        
        // Normalize dates to compare (year, month, day only)
        const startYear = startDate.getFullYear();
        const startMonth = startDate.getMonth();
        const startDay = startDate.getDate();
        
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth();
        const endDay = endDate.getDate();
        
        // Create date objects for comparison (set to start of day)
        const startDateObj = new Date(startYear, startMonth, startDay, 0, 0, 0, 0);
        const endDateObj = new Date(endYear, endMonth, endDay, 23, 59, 59, 999);
        
        const filtered = usersDataState.filter(user => {
            // Ensure user exists and has required properties
            if (!user || !user.user_id) return false;
            if (!user.join_date_original && !user.join_date) return false;
            
            try {
                let userYear, userMonth, userDay;
                
                // Try to use original date first (from API)
                if (user.join_date_original) {
                    const dateStr = user.join_date_original.toString().trim();
                    
                    // Try to parse ISO date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
                    // Extract date parts directly from string to avoid timezone issues
                    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (isoMatch) {
                        // ISO format: YYYY-MM-DD
                        userYear = parseInt(isoMatch[1]);
                        userMonth = parseInt(isoMatch[2]) - 1; // Month is 0-indexed
                        userDay = parseInt(isoMatch[3]);
                    } else {
                        // Try to parse as Date and extract local date (not UTC)
                        const userDate = new Date(dateStr);
                        
                        // Check if date is valid
                        if (isNaN(userDate.getTime())) {
                            return false;
                        }
                        
                        // Use local date methods (not UTC) to match with DatePicker selection
                        userYear = userDate.getFullYear();
                        userMonth = userDate.getMonth();
                        userDay = userDate.getDate();
                    }
                } else if (user.join_date) {
                    // Fallback to formatted date "DD/MM/YYYY"
                    const [day, month, year] = user.join_date.split('/');
                    if (day && month && year) {
                        userYear = parseInt(year);
                        userMonth = parseInt(month) - 1;
                        userDay = parseInt(day);
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
                
                // Check if user date is within range (inclusive)
                // When startDate and endDate are the same, check for exact match
                const isSameDay = startYear === endYear && startMonth === endMonth && startDay === endDay;
                
                if (isSameDay) {
                    // For same day, check exact match (year, month, day)
                    const matches = userYear === startYear && userMonth === startMonth && userDay === startDay;
                    return matches;
                } else {
                    // For date range, check if within range
                    // Create normalized user date object for comparison
                    const userDateObj = new Date(userYear, userMonth, userDay, 0, 0, 0, 0);
                    const matches = userDateObj >= startDateObj && userDateObj <= endDateObj;
                    return matches;
                }
            } catch (e) {
                console.error('Error filtering by date:', e, user);
                return false;
            }
        }).filter(user => user && user.user_id); // Ensure all filtered users are valid
        
        // Debug logging
        if (startDate && endDate) {
            const isSameDay = startYear === endYear && startMonth === endMonth && startDay === endDay;
            console.log('=== Date Filter Debug ===');
            console.log('Filter criteria:', {
                startDate: `${startYear}-${String(startMonth + 1).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`,
                endDate: `${endYear}-${String(endMonth + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`,
                isSameDay: isSameDay
            });
            console.log('Total users:', usersDataState.length);
            console.log('Filtered count:', filtered.length);
            console.log('Sample user dates:', usersDataState.slice(0, 10).map(u => {
                let parsedYear, parsedMonth, parsedDay, parseMethod;
                if (u.join_date_original) {
                    const dateStr = u.join_date_original.toString().trim();
                    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (isoMatch) {
                        parsedYear = parseInt(isoMatch[1]);
                        parsedMonth = parseInt(isoMatch[2]) - 1;
                        parsedDay = parseInt(isoMatch[3]);
                        parseMethod = 'ISO string';
                    } else {
                        const d = new Date(dateStr);
                        parsedYear = d.getFullYear();
                        parsedMonth = d.getMonth();
                        parsedDay = d.getDate();
                        parseMethod = 'Date object';
                    }
                } else if (u.join_date) {
                    const [day, month, year] = u.join_date.split('/');
                    parsedYear = parseInt(year);
                    parsedMonth = parseInt(month) - 1;
                    parsedDay = parseInt(day);
                    parseMethod = 'DD/MM/YYYY';
                }
                const matches = isSameDay 
                    ? (parsedYear === startYear && parsedMonth === startMonth && parsedDay === startDay)
                    : (new Date(parsedYear, parsedMonth, parsedDay, 0, 0, 0, 0) >= startDateObj && new Date(parsedYear, parsedMonth, parsedDay, 0, 0, 0, 0) <= endDateObj);
                return {
                    original: u.join_date_original,
                    formatted: u.join_date,
                    parsed: `${parsedYear}-${String(parsedMonth + 1).padStart(2, '0')}-${String(parsedDay).padStart(2, '0')}`,
                    parseMethod: parseMethod,
                    matches: matches
                };
            }));
            console.log('========================');
        }
        
        return filtered;
    }, [usersDataState, startDate, endDate]);

    const columns = [
        {
            dataField: "user_id",
            text: "ID",
            hidden: true,
            headerClasses: "align-middle font-weight-500 text-nowrap text-center",
            classes: "align-middle"
        },
        {
            dataField: "first_name",
            text: "First Name",
            headerClasses: "align-middle font-weight-500 text-nowrap",
            classes: "align-middle",
            style: { width: "94px" },
            sort: true,
            sortCaret: (order, column) => <SortCaret order={order} />
        },
        {
            dataField: "last_name",
            text: "Last Name",
            headerClasses: "align-middle font-weight-500 text-nowrap",
            classes: "align-middle",
            style: { width: "239px" },
            sort: true,
            sortCaret: (order, column) => <SortCaret order={order} />
        },
        {
            dataField: "email",
            text: "Email",
            headerClasses: "align-middle font-weight-500 text-nowrap",
            classes: "align-middle",
            style: { minWidth: "300px", width: "auto" },
            sort: true,
            sortCaret: (order, column) => <SortCaret order={order} />
        },
        {
            dataField: "ward",
            text: "Ward",
            headerClasses: "align-middle font-weight-500 text-nowrap",
            classes: "align-middle",
            style: { width: "80px" },
            sort: true,
            sortCaret: (order, column) => <SortCaret order={order} />,
            formatter: (cell) => cell || "-"
        },
        {
            dataField: "join_date",
            text: "Joined Date",
            headerClasses: "align-middle font-weight-500 text-nowrap",
            classes: "align-middle",
            style: { width: "140px" },
            sort: true,
            sortCaret: (order, column) => <SortCaret order={order} />
        },
        {
            dataField: "status",
            text: "Status",
            headerClasses: "align-middle font-weight-500 text-nowrap text-center",
            classes: "align-middle",
            style: { width: "120px" },
            formatter: (cell, row) => {
                // Normalize status: "inactive" -> "Inactive", "active" -> "Active"
                const normalizedStatus = cell ? cell.charAt(0).toUpperCase() + cell.slice(1).toLowerCase() : "";
                return (
                    <StatusCellWrapper>
                        <StatusBadge status={normalizedStatus} />
                    </StatusCellWrapper>
                );
            }
        },
        {
            dataField: "Actions",
            text: "Actions",
            style: { width: "140px", whiteSpace: "nowrap" },
            headerClasses: "align-middle font-weight-500 text-nowrap text-center",
            classes: "align-middle",
            formatter: (cell, row) => {
                const handleDeactivateClick = () => {
                    setSelectedUser(row);
                    setModal(prevState => ({ ...prevState, deactivate: true }));
                };
                // Check if user is inactive (handle both "inactive" and "Inactive")
                const status = row?.status?.toLowerCase() || "";
                const isDeactivated = status === "inactive" || status === "deactivated";
                return (
                    <ActionsCellWrapper>
                        <DeactivateButton 
                            onClick={handleDeactivateClick}
                            isDeactivated={isDeactivated}
                        />
                    </ActionsCellWrapper>
                );
            },
        },
    ];

    // Custom Pagination Component
    const CustomPagination = ({ paginationProps }) => (
        <PaginationListStandalone
            {...paginationProps}
            noBorder={true}
        />
    );

    // Removed selectRow and SelectionRowRenderer - checkbox column is hidden


    // Format date range for display: "01/12/2025 - 04/12/2025" or "01/12/2025" if same date
    const formatDateRange = (start, end) => {
        if (!start || !end) return '';
        
        const formatDate = (date) => {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        };
        
        // Check if start and end dates are the same day
        const startYear = start.getFullYear();
        const startMonth = start.getMonth();
        const startDay = start.getDate();
        
        const endYear = end.getFullYear();
        const endMonth = end.getMonth();
        const endDay = end.getDate();
        
        // If same day, month, and year, show only one date
        if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
            return formatDate(start);
        }
        
        return `${formatDate(start)} - ${formatDate(end)}`;
    };

    // Custom Input for DatePicker
    const CustomDateInput = forwardRef(({ value, onClick }, ref) => {
        const displayValue = startDate && endDate ? formatDateRange(startDate, endDate) : (value || 'Start Date - End Date');
        return (
            <DatePickerButton
                ref={ref}
                onClick={onClick}
                type="button"
                value={displayValue}
            >
                <DatePickerText>{displayValue}</DatePickerText>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
                    <path d="M12.75 6.08333H0.75M9.41667 0.75V3.41667M4.08333 0.75V3.41667M3.95 14.0833H9.55C10.6701 14.0833 11.2302 14.0833 11.658 13.8653C12.0343 13.6736 12.3403 13.3676 12.532 12.9913C12.75 12.5635 12.75 12.0034 12.75 10.8833V5.28333C12.75 4.16323 12.75 3.60318 12.532 3.17535C12.3403 2.79903 12.0343 2.49307 11.658 2.30132C11.2302 2.08333 10.6701 2.08333 9.55 2.08333H3.95C2.8299 2.08333 2.26984 2.08333 1.84202 2.30132C1.46569 2.49307 1.15973 2.79903 0.967987 3.17535C0.75 3.60318 0.75 4.16323 0.75 5.28333V10.8833C0.75 12.0034 0.75 12.5635 0.967987 12.9913C1.15973 13.3676 1.46569 13.6736 1.84202 13.8653C2.26984 14.0833 2.8299 14.0833 3.95 14.0833Z" stroke="#475467" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </DatePickerButton>
        );
    });

    const { SearchBar } = Search;


    const handleDeactivateUser = async () => {
        if (!selectedUser) return;
        
        // Handle bulk deactivation
        if (selectedUsersForBulk && selectedUsersForBulk.length > 0) {
            const usersToDeactivate = usersDataState.filter(user => 
                selectedUsersForBulk.includes(user.user_id)
            );
            
            setDeactivationType('bulk');
            setDeactivationInProgress(true);
            
            // Call Redux action for each user and wait for all to complete
            const deactivatePromises = usersToDeactivate.map(user => 
                dispatch(actDeactivateUserAccount(user.email))
            );
            
            try {
                // Wait for all requests to complete
                const results = await Promise.allSettled(deactivatePromises);
                
                // Check results - each result.value should be the response if fulfilled
                const successful = results.filter(result => 
                    result.status === 'fulfilled' && result.value?.status === 200
                );
                
                if (successful.length === results.length) {
                    // All succeeded - update UI
                    setUsersDataState(prevState => 
                        prevState.map(user => 
                            selectedUsersForBulk.includes(user.user_id)
                                ? { ...user, status: "Deactivated" }
                                : user
                        )
                    );
                    toggleModal('deactivate');
                    setRowChecked([]);
                    setSelectedUser(null);
                    setSelectedUsersForBulk([]);
                    fetchDataUsers(); // Refresh data from API
                } else {
                    console.error("Some deactivation requests failed");
                    // Still update UI for successful ones
                    setUsersDataState(prevState => 
                        prevState.map(user => 
                            selectedUsersForBulk.includes(user.user_id)
                                ? { ...user, status: "Deactivated" }
                                : user
                        )
                    );
                    toggleModal('deactivate');
                    setRowChecked([]);
                    setSelectedUser(null);
                    setSelectedUsersForBulk([]);
                }
            } catch (error) {
                console.error("Error in bulk deactivation:", error);
            } finally {
                setDeactivationInProgress(false);
                setDeactivationType(null);
            }
        } else if (selectedUser) {
            // Single user deactivation - use Redux state approach
            const user = usersDataState.find(u => u.user_id === selectedUser.user_id);
            if (!user || !user.email) {
                console.error("User email not found");
                return;
            }
            
            setDeactivationType('single');
            setDeactivationInProgress(true);
            setPendingUserIds([]);
            // Use Redux action - useEffect will handle success
            dispatch(actDeactivateUserAccount(user.email));
        }
    };

    const handleDeactivateSelected = () => {
        if (!rowChecked || rowChecked.length === 0) return;
        
        setSelectedUsersForBulk(rowChecked);
        setSelectedUser({ user_id: rowChecked[0] }); // Set first user for modal display
        setModal(prevState => ({ ...prevState, deactivate: true }));
    };

    const handleClearSelection = () => {
        setRowChecked([]);
    };

    const getDeactivateMessage = () => {
        if (selectedUsersForBulk && selectedUsersForBulk.length > 0) {
            return `${selectedUsersForBulk.length} selected users`;
        }
        if (selectedUser) {
            const user = usersDataState.find(u => u.user_id === selectedUser.user_id);
            return user ? `${user.first_name} ${user.last_name}` : '';
        }
        return '';
    };

    return (
        <React.Fragment>
            <TableHeaderUsers
                {...props}
                usersDataState={usersDataState}
            />
            <TableContainer className="table-food table-custom-makansafe">
                {isLoadingUsers && <LoaderText />}
                <ToolkitProvider
                    keyField="user_id"
                    data={filteredUsersData || []}
                    columns={columns}
                    search
                >
                    {(toolkitprops) => {
                         const searchText = toolkitprops.searchProps?.searchText || '';
                         
                         // Manually filter data to get correct count (ToolkitProvider doesn't update baseProps.data)
                         let searchFilteredData = filteredUsersData || [];
                         if (searchText) {
                             const searchTextLower = searchText.toLowerCase();
                             searchFilteredData = filteredUsersData.filter(row => {
                                 const searchableText = [
                                     row.first_name,
                                     row.last_name,
                                     row.email,
                                     row.ward,
                                     row.join_date,
                                     row.status
                                 ].filter(Boolean).join(' ').toLowerCase();
                                 
                                 return searchableText.includes(searchTextLower);
                             });
                         }
                         
                         const totalSize = searchFilteredData.length;
                         const paginationConfig = {
                            paginationComponent: CustomPagination,
                            totalSize: totalSize
                        };
                        const finalPaginationOptions = paginationOptions({
                            paginationComponent: paginationConfig.paginationComponent,
                            totalSize: paginationConfig.totalSize
                        });

                        return (
                            <ContentTableWrapper>
                                <SearchContainerStyles />
                                {/* Search UI - Outside PaginationProvider to prevent focus loss on remount */}
                                <SearchContainer className="search-container">
                                    <SearchFieldArea className="serach_field-area">
                                        <SearchInner className="search_inner">
                                            <SearchField className="search_field">
                                                <SearchFieldInner>
                                                    {!toolkitprops?.searchProps?.searchText && !isSearchFocused && (
                                                        <SearchIconContainer>
                                                            <Iconsax iconName="search" fill="#667085" size={20} />
                                                        </SearchIconContainer>
                                                    )}
                                                    <SearchInputContainer>
                                                        <SearchBar 
                                                            {...toolkitprops.searchProps}
                                                            placeholder=""
                                                            style={{ 
                                                                flex: '1 1 0%', 
                                                                minWidth: 0, 
                                                                width: '100%', 
                                                                paddingLeft: (!toolkitprops?.searchProps?.searchText && !isSearchFocused) ? '32px' : '0px' 
                                                            }}
                                                        />
                                                        {!toolkitprops?.searchProps?.searchText && !isSearchFocused && (
                                                            <FakePlaceholder className="search_field__fake-placeholder">Search</FakePlaceholder>
                                                        )}
                                                    </SearchInputContainer>
                                                    {toolkitprops?.searchProps?.searchText && (
                                                        <ClearButton className="search_field__clear" onClick={toolkitprops.searchProps.onClear}>
                                                            <Iconsax iconName="close-box" fill="#98A2B3" size={20} />
                                                        </ClearButton>
                                                    )}
                                                </SearchFieldInner>
                                            </SearchField>
                                        </SearchInner>
                                    </SearchFieldArea>
                                    <DatePickerWrapper>
                                        <DatePicker
                                            selected={startDate}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(dates) => {
                                                const [start, end] = dates;
                                                setStartDate(start);
                                                setEndDate(end);
                                            }}
                                            selectsRange
                                            customInput={<CustomDateInput />}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="Start Date - End Date"
                                        />
                                        {startDate && endDate && (
                                            <ClearFiltersButton onClick={() => {
                                                setStartDate(null);
                                                setEndDate(null);
                                            }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M12 4L4 12" stroke="#667085" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M4 4L12 12" stroke="#667085" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Clear filters
                                            </ClearFiltersButton>
                                        )}
                                    </DatePickerWrapper>
                                    {rowChecked?.length > 0 && (
                                        <>
                                            <FilterSpacer />
                                            <FilterContainerUsers
                                                rowChecked={rowChecked}
                                                handleDeactivateSelected={handleDeactivateSelected}
                                                handleClearSelection={handleClearSelection}
                                            />
                                        </>
                                    )}
                                </SearchContainer>

                                {/* Table and Pagination - key prop forces reset when search text changes */}
                                <PaginationProvider 
                                    key={searchText}
                                    pagination={paginationFactory(finalPaginationOptions)}
                                >
                                    {({ paginationProps, paginationTableProps }) => (
                                        <React.Fragment>
                                            <BootstrapTable
                                                wrapperClasses="table-responsive"
                                                bordered={false}
                                                columns={columns}
                                                keyField="user_id"
                                                rowStyle={(row, rowIndex) => {
                                                    // Change row color if status is inactive
                                                    const status = row?.status?.toLowerCase() || "";
                                                    if (status === "inactive" || status === "deactivated") {
                                                        return {
                                                            color: '#B1B1B1'
                                                        };
                                                    }
                                                    return {};
                                                }}
                                                noDataIndication={() => (
                                                    <DataEmty content="No Data" />
                                                )}
                                                {...toolkitprops.baseProps}
                                                {...paginationTableProps}
                                            />
                                            <PaginationWrapper>
                                                <PaginationListStandalone {...paginationProps} />
                                            </PaginationWrapper>
                                        </React.Fragment>
                                    )}
                                </PaginationProvider>
                            </ContentTableWrapper>
                        );
                    }}
                </ToolkitProvider>
            </TableContainer>
            <DeactivateModal
                isOpen={modal?.deactivate}
                toggle={() => toggleModal('deactivate')}
                isLoading={isLoadingDeactivate || deactivationInProgress}
                handleRemove={handleDeactivateUser}
                userName={getDeactivateMessage()}
                isBulk={selectedUsersForBulk && selectedUsersForBulk.length > 0}
            />
        </React.Fragment>
    );
}

export default TableUsers;

